/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, InputAdornment, useMediaQuery } from '@mui/material';
import Dropdown, { DropdownOption } from '../ui/dropdown';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useSettings } from '../../providers/settings-provider';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import theme from '../../util/theme/theme';

interface AddBankAccountPopupProps {
    open: boolean;
    onClose: () => void;
    onCreate: (data: { name: string; bankId: number | null; openingBalance: number }) => void;
    isEdit?: { id: number | null; state: boolean; }
    existing?: { id: number; name: string; bankId: number }[];
    bankFilter?: (bank: any) => boolean;
    forImport?: boolean;
}

const AddBankAccountPopup: React.FC<AddBankAccountPopupProps> = ({ open, onClose, onCreate, isEdit, existing, bankFilter, forImport }) => {
    const { companySettings } = useSettings();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [accountName, setAccountName] = useState('');
    const [bank, setBank] = useState('');
    const [openingBalance, setOpeningBalance] = useState<string>('');
    const [banks, setBanks] = useState<DropdownOption[]>([]);
    const [errors, setErrors] = useState<{ accountName?: string; bank?: string; openingBalance?: string }>({});
    const [originalAccountName, setOriginalAccountName] = useState('');
    const [originalBank, setOriginalBank] = useState('');
    const [originalOpeningBalance, setOriginalOpeningBalance] = useState<string>('');

    const [fetchAccount, setFetchAccount] = useApi();
    const [fetchBanks, setFetchBanks] = useApi();

    React.useEffect(() => {
        if(!open) return;
        const { url, method } = buildApiRequestConfig(routes.bank.fetch, [{ param: 'countryId', value: companySettings?.company.countryId || 0 }]);
        setFetchBanks({ url, method });
    }, [open, companySettings]);

    React.useEffect(() => {
        const { data, isLoading } = fetchBanks;
        if (data && !isLoading) {
            const filteredData = bankFilter ? data.filter(bankFilter) : data;
            setBanks(filteredData.map((bank: any) => ({
                value: bank.id,
                label: bank.name
            })));
        }
    }, [fetchBanks, bankFilter]);

    React.useEffect(() => {
        if (isEdit?.state && isEdit.id) {
            const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchById, [{ param: 'id', value: isEdit.id }]);
            setFetchAccount({ url, method });
        }
    }, [isEdit]);

    React.useEffect(() => {
        const { data, isLoading } = fetchAccount;
        if (data && !isLoading) {
            setAccountName(data.accountName);
            setBank(data.bankId);
            setOpeningBalance(data.openingBalance.toString());
            // Store original values for change detection
            setOriginalAccountName(data.accountName);
            setOriginalBank(data.bankId);
            setOriginalOpeningBalance(data.openingBalance.toString());
        }
    }, [fetchAccount]);

    // Check if any changes have been made in edit mode
    const hasChanges = React.useMemo(() => {
        if (!isEdit?.state) {
            // For create operations, check if required fields are filled
            return !!accountName && !!bank;
        }
        
        // For edit operations, check if anything changed
        return (
            accountName !== originalAccountName ||
            bank !== originalBank ||
            openingBalance !== originalOpeningBalance
        );
    }, [isEdit?.state, accountName, originalAccountName, bank, originalBank, openingBalance, originalOpeningBalance]);

    const handleCreate = () => {
        const newErrors: { accountName?: string; bank?: string; openingBalance?: string } = {};
		if (!accountName) {
            newErrors.accountName = 'Account name is required';
        } else {
            if (accountName.length > 50) {
                newErrors.accountName = 'Account name exceeds 50 character limit'
            }
            
            // Check for duplicate account name within the same bank
            const isNameChanged = isEdit?.state ? accountName !== originalAccountName : true;
            if (existing && isNameChanged) {
                const duplicateAccount = existing.find(item => 
                    item.name.toLowerCase() === accountName.toLowerCase() && 
                    item.bankId === Number(bank) &&
                    (!isEdit?.state || item.id !== isEdit.id) // Exclude current account in edit mode
                );
                
                if (duplicateAccount) {
                    newErrors.accountName = 'An account with this name already exists for the selected bank.';
                }
            }
        }
		if (!bank) newErrors.bank = 'Bank is required';
        
        const balanceNum = openingBalance ? parseFloat(openingBalance) : null;
        if (openingBalance) {
            if (balanceNum === null || isNaN(balanceNum)) {
                newErrors.openingBalance = 'Opening balance must be a number';
            } else if (!/^\d+(\.\d{1,2})?$/.test(balanceNum.toFixed(2))) {
                newErrors.openingBalance = 'Opening balance can have up to 2 decimal places';
            }
        }
        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            onCreate({ 
                name: accountName, 
                bankId: Number(bank),
                openingBalance: balanceNum || 0.00
            });
            setAccountName('');
            setBank('');
            setOpeningBalance('');
        }
    };

    const handleClose = () => {
        setAccountName('');
        setBank('');
        setOpeningBalance('');
        setOriginalAccountName('');
        setOriginalBank('');
        setOriginalOpeningBalance('');
        setErrors({});
        onClose();
    };

    const content = (
        <>
        {forImport ? (
            <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 20}}>No Bank Account Exist</Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Please add one</Typography>
            </Box>
        ) : (
            <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 1 }}>{isEdit?.state ? 'Edit' : 'Add New'} Account</DialogTitle>
        )}
        <DialogContent sx={{ pt: forImport ? 0 : 1 }}>
            <Box mb={2}>
                <Typography fontSize={15} color="var(--color-text-muted)">
                    Bank <span style={{ color: '#F44336' }}>*</span>
                </Typography>
                <Dropdown
                    options={banks}
                    value={bank}
                    onChange={(value) => setBank(value)}
                    placeholder="Select bank"
                    error={!!errors.bank}
                    helperText={errors.bank}
                    height={40}
                />
            </Box>
            <Box mb={2}>
                <Typography fontSize={15} color="var(--color-text-muted)">
                    Account Name <span style={{ color: '#F44336' }}>*</span>
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter account name"
                    value={accountName}
                    onChange={e => setAccountName(e.target.value)}
                    error={!!errors.accountName}
                    helperText={errors.accountName}
                    sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    inputProps={{ maxLength: 50 }}
                />
            </Box>
            <Box mb={2}>
                <Typography fontSize={15} color="var(--color-text-muted)">
                    Opening Balance <span style={{ color: '#F44336' }}>*</span>
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={openingBalance}
                    onChange={(e: any) => {
                        const value = e.target.value;
                        // Only allow numbers and one dot
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            setOpeningBalance(value);
                        }
                    }}
                    error={!!errors.openingBalance}
                    helperText={errors.openingBalance}
                    sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Typography sx={{ color: 'var(--color-primary)', fontSize: 18 }}>{companySettings?.settings.currencySymbol}</Typography>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained" sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                Cancel
            </Button>
            <Button 
                onClick={handleCreate} 
                variant="contained" 
                disabled={!hasChanges}
                sx={{ 
                    background: hasChanges ? 'var(--color-primary)' : '#ccc', 
                    color: '#fff', 
                    boxShadow: 'none', 
                    borderRadius: 2, 
                    fontWeight: 600, 
                    width: 140, 
                    whiteSpace: 'nowrap',
                    '&:hover': { 
                        background: hasChanges ? 'var(--color-primary)' : '#ccc' 
                    },
                    '&:disabled': {
                        background: '#ccc',
                        color: 'var(--color-text-muted)'
                    }
                }}
            >
                {isEdit?.state ? 'Save Changes' : 'Add Account'}
            </Button>
        </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
            {content}
        </Dialog>
    );
};

export default AddBankAccountPopup;
